import { useState } from 'react';

import {
  Box,
  Button,
  Card,
  List,
  ListItem,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
  useColorScheme,
} from '@mui/joy';

import projectScreenerOverlayDark from '../assets/images/project-screener-overlay-dark.png';
import projectScreenerOverlay from '../assets/images/project-screener-overlay.png';
import useSubmitScreeningInterest from '../pages/projectScreener/hooks/useSubmitScreeningInterest';
import { useGlobalStore } from '../stores/useGlobalStore';

const BULLETS = [
  {
    title: 'Instant insights:',
    desc: 'Upload a PDD and get an estimated GHG rating range',
  },
  {
    title: 'Screen and compare:',
    desc: 'Narrow a long list of projects to the ones worth deeper review',
  },
  {
    title: 'We surface the red flags that matter:',
    desc: "Based on Calyx Global's rating methodology and every project we've rated to date",
  },
];

const ProjectScreenerOverlay = () => {
  const { mode } = useColorScheme();
  const overlayImage = mode === 'dark' ? projectScreenerOverlayDark : projectScreenerOverlay;
  const userInfo = useGlobalStore((s) => s.user);
  const setSnackbar = useGlobalStore((s) => s.setSnackbar);
  const submitInterest = useSubmitScreeningInterest();
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);

  const handleInterestClick = () => {
    submitInterest.mutate(undefined, {
      onSuccess: () => setConfirmationOpen(true),
      onError: () =>
        setSnackbar({ message: 'Something went wrong. Please try again.', color: 'danger' }),
    });
  };

  return (
    <Box
      sx={{
        backdropFilter: 'blur(12px)',
        backgroundColor: 'rgba(57, 57, 57, 0.05)',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 111,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowX: 'auto',
      }}
    >
      <Card
        variant='outlined'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          p: 4,
          borderColor: (theme) => theme.vars.palette.primary.outlinedBorder,
          boxShadow: (theme) => theme.shadow.md,
          maxWidth: '1131px',
          width: '90%',
          height: 'auto',
          position: 'absolute',
          top: '18%',
          zIndex: 1,
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          alignItems='stretch'
          justifyContent='center'
          gap={4}
          width='100%'
        >
          <Stack
            flex={1}
            alignItems='flex-start'
            justifyContent='space-between'
            gap={5}
            height='100%'
          >
            <Stack gap={1}>
              <Typography level='h3' fontWeight='xl'>
                Get an estimated GHG rating in minutes
              </Typography>
              <Typography
                sx={{
                  color: (theme) => theme.palette.text.tertiary,
                  fontSize: (theme) => theme.vars.fontSize.lg,
                }}
              >
                AI-powered analysis of a project&apos;s PDD
              </Typography>

              <List marker='disc' sx={{ pl: 2 }}>
                {BULLETS.map((item) => (
                  <ListItem key={item.title}>
                    <Typography
                      sx={{
                        color: (theme) => theme.palette.text.secondary,
                        fontSize: (theme) => theme.vars.fontSize.lg,
                      }}
                    >
                      <b>{item.title}</b> {item.desc}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Stack>

            <Stack gap={1}>
              <Typography
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: (theme) => theme.vars.fontSize.md,
                }}
              >
                Interested in testing this feature? Join the Beta.
              </Typography>
              <Button
                variant='solid'
                disabled={submitInterest.isPending}
                sx={{
                  alignSelf: 'flex-start',
                  px: 3,
                  py: 1.5,
                  fontSize: (theme) => theme.vars.fontSize.md,
                  background: (theme) =>
                    `linear-gradient(270deg, ${theme.palette['expert']['dark']} 0%, ${theme.palette['expert']['light']} 100%)`,
                }}
                onClick={handleInterestClick}
              >
                I&apos;m interested
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 0,
            }}
          >
            <img
              src={overlayImage}
              style={{
                width: '100%',
                maxWidth: 584,
                height: 'auto',
                borderRadius: 12,
              }}
              alt='project-screener-preview'
            />
          </Box>
        </Stack>
      </Card>

      <Modal open={isConfirmationOpen} onClose={() => setConfirmationOpen(false)}>
        <ModalDialog sx={{ width: 590, maxWidth: '90vw', p: 3, gap: 2, borderRadius: 'md' }}>
          <ModalClose />
          <Typography level='title-lg' fontWeight='xl'>
            Thanks for your interest!
          </Typography>
          <Typography sx={{ color: (theme) => theme.palette.text.secondary }}>
            Our team will be reaching out to {userInfo?.email} soon.
          </Typography>
          <Stack alignItems='center' mt={2}>
            <Button
              variant='solid'
              color='primary'
              sx={{ minWidth: 180 }}
              onClick={() => setConfirmationOpen(false)}
            >
              Okay
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default ProjectScreenerOverlay;
